// Bowling Green State University: public college page (#2743), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - BGSU's Common Data Set 2025-2026 for the main campus, which the Office
//    of Institutional Research publishes as an interactive dashboard (a
//    Tableau Public workbook, "Common Data Set, BGSU Main Campus", Year
//    filter 2025-2026) embedded in its bgsu.edu Common Data Set page; the
//    SOURCES url is that page. C1 applicants, admits and enrollees for Fall
//    2025 (the dashboard shows men and women only; no residency table) and
//    the early plans section ("Bowling Green State University does not
//    offer Early Decision and Early Action Plans"). The dashboard's G1/G5
//    costs carry no academic year, so they are not used.
//  - BGSU Admissions: Freshman Application Instructions (updated August
//    2026; the fall timing, program rules with Fall 2027 dates, the $45
//    fee) and the Test Optional page.
//    Quirk: the two disagree on testing. The instructions list official
//    ACT or SAT results among the required materials, yet on the same page
//    set test-optional paths for business and nursing; the Test Optional
//    page (updated September 2025) says applications can be reviewed
//    without scores but names only the 2023-2024 and 2024-2025 terms. The
//    page is marked test-optional and the line says all of this.
//  - BGSU Financial Aid, Cost of Attendance: "Undergraduate BG Campus -
//    2026-27 Cohort", on-campus students. Out-of-state adds the $7,990
//    non-resident fee, folded into the tuition line here.
//  - College Scorecard, school id 201441 ("Bowling Green State
//    University-Main Campus", net price by family income), read from the
//    Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'BGSU Common Data Set 2025-2026, Main Campus (dashboard)',
        url: 'https://www.bgsu.edu/institutional-research/CDS.html',
        read: '2026-10-01',
    },
    instructions: {
        label: 'BGSU Admissions, Freshman Application Instructions',
        url: 'https://www.bgsu.edu/admissions/apply-now/freshmen/application-instructions.html',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'BGSU Admissions, Test Optional',
        url: 'https://www.bgsu.edu/admissions/test-optional.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'BGSU Financial Aid, Cost of Attendance',
        url: 'https://www.bgsu.edu/financial-aid/cost-of-attendance.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?201441-Bowling-Green-State-University-Main-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'bowling-green-state-university',
    kbId: 'bowling_green_state_university',
    name: 'Bowling Green State University',
    shortName: 'Bowling Green',
    aliases: ['BGSU'],
    location: 'Bowling Green, OH',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (BGSU official identity colors); decoration only.
    branding: {
        primary: '#FD5000',
        secondary: '#4F2C1D',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/bowling_green_state_university_logo.png',
    },

    // The direct answer and short FAQ (#4090, founder 2026-10-13: content
    // only, no title change before the Oct 27 title-test read). Every figure
    // restates one the page already shows from the source named beside it;
    // CollegePages.test.jsx fails a number the rest of the page does not hold.
    // The search it answers: bowling green state university acceptance rate.
    faq: {
        answer: { text: 'Bowling Green State University (BGSU) admitted 81.3% of first-year applicants to its main campus for fall 2025, 17,836 of the 21,938 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1 (main campus)' },
        questions: [
            { q: 'What is the BGSU acceptance rate?', a: 'For fall 2025 entry, BGSU\'s main campus admitted 17,836 of 21,938 first-year applicants, an acceptance rate of 81.3%, and 3,577 enrolled. BGSU reports no rate by college or major.', source: 'cds', where: 'section C1 (main campus)' },
            { q: 'When should I apply to BGSU?', a: 'BGSU encourages you to apply from July 1 between junior and senior year and to have your application and all credentials in by March 1 to assure consideration for fall 2027. The application fee is $45.', source: 'instructions' },
            { q: 'Do some BGSU programs have a higher bar?', a: 'Yes. Direct admission to Nursing asks for a 3.75 high school GPA and ACT 23 or equivalent, or a 3.95 GPA without scores, by January 15. Forensic Science asks for a 3.75 GPA, and Aviation Flight Operations has its own review after you are admitted.', source: 'instructions' },
            { q: 'Does BGSU require the SAT or ACT?', a: 'BGSU\'s pages disagree. Its Test Optional page says applications can be reviewed without scores, while its freshman application instructions, updated August 2026, still list official ACT or SAT results as required. Ask admissions before you rely on either.', source: 'testOptional' },
            { q: 'What does a year at BGSU cost?', a: 'For 2026-2027, a new undergraduate living on the Bowling Green campus comes to $33,900 in-state and $41,890 out-of-state, at the Falcon Tuition Guarantee rate, locked for up to four years.', source: 'costs' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 21938,
        admitted: 17836,
        enrolled: 3577,
        source: 'cds',
        where: 'section C1 (main campus)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'BGSU\'s Common Data Set reports one first-year acceptance rate for the main campus, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'BGSU has no early decision or early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to BGSU. Several programs have their own step or standard:',
        rows: [
            { school: 'Aviation Flight Operations', requirement: 'A secondary review by the program after you are admitted, with essay questions due December 9, 2026 for Fall 2027. Space is limited and there is no waitlist; students not admitted move to aviation management operations.' },
            { school: 'College of Musical Arts', requirement: 'An audition, in person (highly preferred) or by recorded video, for every student who wishes to major or minor in music, as well as the BGSU application.' },
            { school: 'Musical Theatre', requirement: 'An audition, in person (highly preferred) or recorded, to be accepted to the major.' },
            { school: 'Computer Science and Software Engineering', requirement: 'Direct admission with an SAT math score of 550, an ACT math score of 22, or MATH 1220 (College Algebra) with a C or better, plus high grades in high school math. Otherwise you start in pre-computer science or software engineering.' },
            { school: 'Forensic Science (DNA Analysis, Drug Analysis, Examination)', requirement: 'A 3.75 high school GPA or higher. Otherwise some students are offered pre-forensic science and others move to the Deciding Students Program.' },
            { school: 'Schmidthorst College of Business', requirement: 'Direct admission with at least an 18 ACT total and 18 ACT math, or an SAT total of 970 and 500 math; test optional: a 3.0 GPA and a C or better in Algebra I, Geometry and Algebra II. Otherwise you start in the Pre-Business Program.' },
            { school: 'Nursing', requirement: 'Direct admission with a 3.75 high school GPA and ACT 23 (or equivalent), or a 3.95 GPA for test-optional students; apply for direct admission by January 15. You must keep a 3.5 BGSU GPA to hold the seat.' },
            { school: 'Honors College', requirement: 'Selective, with its own application after you are admitted to the university.' },
        ],
        source: 'instructions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Complete by, to assure consideration', value: 'March 1' },
        rows: [
            { plan: 'Fall application', deadline: 'March 1, to assure consideration', decision: null, binding: false },
        ],
        note: 'BGSU encourages high school students to apply starting July 1 between junior and senior year, and to have the application and all credentials in by March 1 to assure consideration for fall. It does not publish a decision date. The application fee is $45. Program steps above have their own dates.',
        source: 'instructions',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'BGSU\'s pages disagree, so ask admissions before you rely on either. Its Test Optional page says applications can be reviewed without scores, that a score you send is not considered if you apply test optional, and that BGSU superscores, but it names only the 2023-2024 and 2024-2025 terms. Its freshman application instructions, updated August 2026, still list official ACT or SAT results as required of all freshmen, while setting test-optional standards for business and nursing on the same page.',
        source: 'testOptional',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'instructions',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (out-of-state includes the $7,990 non-resident fee)', amounts: [14774, 22764] },
            { item: 'Fees', amounts: [730, 730] },
            { item: 'Housing (on campus)', amounts: [8160, 8160] },
            { item: 'Food (on campus)', amounts: [6016, 6016] },
            { item: 'Books and supplies', amounts: [860, 860] },
            { item: 'Loan fees', amounts: [40, 40] },
            { item: 'Personal and miscellaneous', amounts: [2400, 2400] },
            { item: 'Transportation', amounts: [920, 920] },
        ],
        totals: [33900, 41890],
        note: 'For a new undergraduate on the Bowling Green campus living on campus, at the Falcon Tuition Guarantee rate for the 2026-27 cohort, locked for up to four years. Housing is the average across BGSU\'s on-campus options, not the amount you are charged, and food is an estimate for three meals a day.',
        source: 'costs',
        netPrice: {
            average: 21453,
            byIncome: [
                { income: '$0 to $30,000', amount: 17881 },
                { income: '$30,001 to $48,000', amount: 16662 },
                { income: '$48,001 to $75,000', amount: 18793 },
                { income: '$75,001 to $110,000', amount: 22860 },
                { income: 'Over $110,000', amount: 25624 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from BGSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
